import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Alert,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Stack,
    Typography
} from "@mui/material";
import { FormEvent, useState } from "react";
import { doApiPost } from "../../api/Requests";
import { AuthBox, FlexRowBox, LeftContent, RightContent, StyledTextField } from "./Authentication.styles";
import BonisoftLogo from "../../common/ui/BonisoftLogo";
import AuthFooter from "./AuthFooter";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PasswordStrength, { passwordStrengthTest } from "./PasswordStrength";
import LanguageSwitcher from "../../common/ui/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import PasswordRules from "./PasswordRules";
import AuthLayout from "./AuthLayout";
import { useLocale } from "../../hooks/UseLocale";
import { GeneralButton } from "../../common/ui/GeneralButton";

interface ResetState {
    password: string,
    passwordRepeated: string,
    email: string,
}

export default function LinkingSetPasswordScreen() {
    const navigate = useNavigate();
    const locale = useLocale();
    const [searchParams] = useSearchParams();
    const emailFromURL = searchParams.get("email") ?? "";
    const token = searchParams.get("token");
    const [resetState, setResetState] = useState<ResetState>({
        password: "",
        passwordRepeated: "",
        email: emailFromURL,
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatedPassword, setShowRepeatedPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0)
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const { t } = useTranslation();

    const isPasswordMatch = resetState.password === resetState.passwordRepeated;
    const isFormValid = isPasswordMatch && resetState.password.length > 0 && passwordStrength >= 3;

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowRepeatedPassword = () => setShowRepeatedPassword((show) => !show);

    const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const onHandleSetPassword = async function (event: FormEvent) {
        event.preventDefault()

        setIsLoading(true);

        const result = await doApiPost<any, never>(
            `/api/users/password_reset/${token}`,
            {
                eMail: resetState.email,
                newPassword: resetState.password
            },
            { handleUnauthenticated: true }
        );

        if (result.success()) {
            navigate(`/${locale}/login`)
        } else {
            setIsError(true);
        }
        setIsLoading(false);
    }

    return (
        <AuthLayout>
            <AuthBox component="form" onSubmit={onHandleSetPassword}>
                <FlexRowBox>
                    <LeftContent>
                        <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h5">
                                    {t("authentication.authSettings.setPassword")}
                                </Typography>
                                <LanguageSwitcher />
                            </Stack>

                            <Typography variant="body2" color="textSecondary">
                                {t("authentication.authSettings.description")}
                            </Typography>

                            <StyledTextField
                                value={resetState.email}
                                disabled
                                InputProps={{ readOnly: true }}
                            />

                            <OutlinedInput
                                required
                                fullWidth
                                placeholder={t("authentication.authSettings.password")}
                                type={showPassword ? "text" : "password"}
                                value={resetState.password}
                                onChange={(e) => {
                                    setPasswordStrength(passwordStrengthTest(e.target.value));
                                    setResetState({ ...resetState, password: e.target.value });
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDown}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />

                            <PasswordStrength strength={passwordStrength} />
                            <PasswordRules pw={resetState.password} />

                            <OutlinedInput
                                required
                                fullWidth
                                placeholder={t("authentication.authSettings.repeatPassword")}
                                type={showRepeatedPassword ? "text" : "password"}
                                value={resetState.passwordRepeated}
                                error={resetState.password !== resetState.passwordRepeated}
                                onChange={(e) =>
                                    setResetState({ ...resetState, passwordRepeated: e.target.value })
                                }
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleClickShowRepeatedPassword}
                                            onMouseDown={handleMouseDown}
                                            edge="end"
                                        >
                                            {showRepeatedPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />

                            {isError && (
                                <Alert severity="error">
                                    {t("authentication.authSettings.setPasswordFailed")}
                                </Alert>
                            )}

                            <GeneralButton
                                fullWidth
                                type="submit"
                                appVariant="primary"
                                loading={isLoading}
                                disabled={!isFormValid}
                            >
                                {t("authentication.authSettings.setPassword")}
                            </GeneralButton>
                        </Stack>
                    </LeftContent>
                    <RightContent>
                        <BonisoftLogo imgRatio={30} />
                    </RightContent>
                </FlexRowBox>
            </AuthBox>
            <AuthFooter />
        </AuthLayout>
    );
}
